import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

export interface MismatchAttrs {
  fragmentResultColor: string;
  fragmentSequenceName: string;
  fragmentSequenceID: string;
  newBase: string;
  onClickFragmentSequenceLink: () => void;
  originalBase: string;
  pos: number;
  showPrintableSummary: boolean;
}

type MismatchRowProps = MismatchAttrs & {
  onClick: () => void;
  selected: boolean;
};

export function Mismatch({
  fragmentResultColor,
  fragmentSequenceName,
  fragmentSequenceID,
  newBase,
  onClick,
  onClickFragmentSequenceLink,
  originalBase,
  pos,
  selected,
  showPrintableSummary,
}: MismatchRowProps): JSX.Element {
  return (
    <tr
      className={selected ? css(styles.clickable, styles.selected) : css(styles.clickable)}
      onClick={onClick}
    >
      <td>{pos}</td>
      <td
        style={{[showPrintableSummary ? 'borderLeftColor' : 'backgroundColor']: fragmentResultColor}}
        className="printable-color"
      >
        {fragmentSequenceID ? (
          <a
            className={css(styles.clickable)}
            data-fragment-sequence-id={fragmentSequenceID}
            onClick={onClickFragmentSequenceLink}
          >
            {fragmentSequenceName}
          </a>
        ) : (
          fragmentSequenceName
        )}
      </td>
      <td>{originalBase}</td>
      <td>{newBase}</td>
    </tr>
  );
}

export interface MismatchesProps {
  mismatches: Array<MismatchAttrs>;
}

export default function Mismatches({mismatches}: MismatchesProps): JSX.Element {
  const [selectedPos, setSelectedPos] = useState<number | null>(null);

  return (
    <fieldset>
      <legend>Mismatches Introduced</legend>
      <div className="row primer-assembly-mismatches">
        <div className="col col-md-12">
          {mismatches.length > 0 && (
            <table className="table table-condensed table-hover">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Parent</th>
                  <th>Original Base</th>
                  <th>New Base</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map(
                  ({
                    fragmentResultColor,
                    fragmentSequenceName,
                    fragmentSequenceID,
                    newBase,
                    onClickFragmentSequenceLink,
                    originalBase,
                    pos,
                    showPrintableSummary,
                  }) => (
                    <Mismatch
                      key={pos}
                      fragmentResultColor={fragmentResultColor}
                      fragmentSequenceName={fragmentSequenceName}
                      fragmentSequenceID={fragmentSequenceID}
                      newBase={newBase}
                      onClick={() => {
                        setSelectedPos(pos);
                      }}
                      onClickFragmentSequenceLink={onClickFragmentSequenceLink}
                      originalBase={originalBase}
                      pos={pos}
                      selected={pos === selectedPos}
                      showPrintableSummary={showPrintableSummary}
                    />
                  )
                )}
              </tbody>
            </table>
          )}
          {mismatches.length === 0 && <p>No mismatches with parent sequences have been introduced.</p>}
        </div>
      </div>
    </fieldset>
  );
}

const styles = StyleSheet.create({
  clickable: {
    cursor: 'pointer',
  },
  selected: {
    backgroundColor: Color.GRAY_50,
  },
});
